import {LitElement, html, css} from "lit";
import { panel } from "../../styles";
import { fa } from "../../../utils/widgets";
import { faInfoCircle } from "@fortawesome/free-solid-svg-icons/faInfoCircle";
import PanoramaxImg from "../../../img/panoramax.svg";
import { classMap } from "lit/directives/class-map.js";

/**
 * Legend widget, handling switch between map and photo components.
 * Also displays a default "About Panoramax" message.
 * @class Panoramax.components.ui.widgets.Legend
 * @element pnx-widget-legend
 * @extends [lit.LitElement](https://lit.dev/docs/api/LitElement/)
 * @slot `editors` External links to map editors, or any tool that may be helpful. Defaults to OSM tools (iD & JOSM).
 * @example
 * ```html
 * <!-- Default legend -->
 * <pnx-widget-legend
 *   _parent=${viewer}
 *   focus="map"
 *   picture="PICTURE-ID-IF-ANY"
 * />
 * 
 * <!-- With custom editor links -->
 * <pnx-widget-legend
 *   _parent=${viewer}
 *   focus="map"
 *   picture="PICTURE-ID-IF-ANY"
 * >
 *   <div slot="editors"><a href="http://my.own.tool">Edit in my own tool</a></div>
 * </pnx-widget-legend>
 * ```
 */
export default class Legend extends LitElement {
	/** @private */
	static styles = [panel, css`
		.pnx-panel[part="panel"] {
			border-radius: 10px;
			position: relative;
			max-width: 80vw;
			z-index: 121; /* To appear above mini component */
			min-width: unset;
		}
		.pnx-panel[part="panel"].pnx-padded {
			padding: 10px;
			width: 250px;
		}
		.presentation {
			font-size: 0.9em;
			display: flex;
			gap: 5px;
			align-items: center;
		}
		.logo {
			width: 45px;
		}
	`];

	/**
	 * Component properties.
	 * @memberof Panoramax.components.ui.widgets.Legend#
	 * @type {Object}
	 * @property {string} [focus] The focused main component (map, pic)
	 * @property {string} [picture] The picture ID
	 */
	static properties = {
		focus: {type: String},
		picture: {type: String},
	};

	render() {
		const classes = {
			"pnx-panel": true,
			"pnx-padded": this.focus == "map",
		};

		return html`<div class=${classMap(classes)} part="panel">
			<div
				class="presentation"
				style=${this.focus != "map" && this.picture ? "display: none": ""}
			>
				<img class="logo" src=${PanoramaxImg} alt="" />
				<div>
					${this._parent?._t.pnx.whats_panoramax}
					<pnx-link-button
						title=${this._parent?._t.map.more_panoramax}
						kind="superinline"
						href="https://panoramax.fr"
						target="_blank"
						size="sm"
					>
						${fa(faInfoCircle)}
					</pnx-link-button>
				</div>
			</div>
			<pnx-picture-legend
				._parent=${this._parent}
				style=${this.focus == "map" ? "display: none": ""}
				collapsable
			>
				<slot slot="editors" name="editors">
					<pnx-widget-osmeditors ._parent=${this._parent} />
				</slot>
			</pnx-picture-legend>
			<pnx-map-legend
				._parent=${this._parent}
				style=${this.focus != "map" ? "display: none": ""}
			></pnx-map-legend>
		</div>`;
	}
}

customElements.define("pnx-widget-legend", Legend);
